import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Download, TrendingUp, Shield, Users, UserCircle } from 'lucide-react';

const stats = [
  { icon: TrendingUp, value: '$50B+', label: 'Market Opportunity' },
  { icon: Users, value: '12M+', label: 'Active Renters' },
  { icon: Shield, value: '98%', label: 'Satisfaction Rate' },
];

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden">
      {/* Animated Background with Parallax */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <div className="absolute top-0 -left-40 w-[600px] h-[600px] bg-gradient-to-br from-blue-600/20 via-cyan-500/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -right-40 w-[600px] h-[600px] bg-gradient-to-tl from-purple-600/20 via-pink-500/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/5 via-cyan-500/5 to-blue-500/5 rounded-full blur-3xl animate-pulse-slow"></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" 
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}>
        </div>
      </motion.div>

      <motion.div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40" style={{ y: contentY, opacity }}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
              <span className="text-blue-400 text-sm font-medium">🚀 The Future of Renting</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Smart Rent.{' '}
              <span className="text-gradient">Smart Living.</span>
              <br />
              <span className="text-gradient-secondary">For Everyone.</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              The all-in-one PropTech platform transforming rental living. 
              Pay rent, get insurance, report maintenance, and build wealth—all from one app.
            </motion.p>

            {/* Trust Signal */}
            <motion.div
              className="flex items-center gap-4 mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex -space-x-3">
                <div className="w-10 h-10 rounded-full border-2 border-dark bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <UserCircle className="text-white" size={20} />
                </div>
                <div className="w-10 h-10 rounded-full border-2 border-dark bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <UserCircle className="text-white" size={20} />
                </div>
                <div className="w-10 h-10 rounded-full border-2 border-dark bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                  <UserCircle className="text-white" size={20} />
                </div>
                <div className="w-10 h-10 rounded-full border-2 border-dark bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <UserCircle className="text-white" size={20} />
                </div>
              </div>
              <span className="text-sm text-gray-400">
                Join <span className="text-white font-semibold">12M+</span> renters & managers across Africa
              </span>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <a href="https://play.google.com/store/apps/details?id=com.yawote.app" target="_blank" rel="noopener noreferrer">
                <button className="btn-primary flex items-center justify-center gap-2 text-base px-8 py-4 group">
                  <Download size={20} className="group-hover:scale-110 transition-transform" />
                  Get Started — Download YAWOTE
                </button>
              </a>
              <a href="/partner-form">
                <button className="btn-secondary flex items-center justify-center gap-2 text-base px-8 py-4 group">
                  Become a Partner
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </a>
            </motion.div>
          </motion.div>

          {/* Right Content - Phone Mockups */}
          <motion.div
            className="relative hidden lg:block"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative">
              {/* Main phone mockup - app dashboard */}
              <motion.div
                className="relative mx-auto w-[280px] h-[580px] rounded-[36px] overflow-hidden shadow-2xl shadow-blue-500/20"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="absolute inset-0 bg-black rounded-[36px] border-4 border-slate-700/60 p-1.5">
                  <div className="w-full h-full rounded-[28px] overflow-hidden relative">
                    <img src="/login.jpeg" alt="YAWOTE Dashboard" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full"></div>
              </motion.div>

              {/* Floating preview - payment screen */}
              <motion.div
                className="absolute -bottom-6 -left-10 w-[180px] rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                animate={{ y: [0, 10, 0], x: [0, -5, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                style={{ rotate: '-8deg' }}
              >
                <img src="/app-payment.png" alt="YAWOTE Payment" className="w-full object-cover" />
              </motion.div>

              {/* Floating preview - another screen */}
              <motion.div
                className="absolute -top-4 -right-8 w-[160px] rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                animate={{ y: [0, -10, 0], x: [0, 5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                style={{ rotate: '6deg' }}
              >
                <img src="/phone-mockup-1.jpeg" alt="YAWOTE Screen" className="w-full object-cover" />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Stats Bar */}
        <motion.div
          className="grid grid-cols-3 gap-8 mt-20 max-w-2xl mx-auto lg:mx-0"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          {stats.map((stat, idx) => {
            const IconComponent = stat.icon;
            return (
              <div key={idx} className="text-center">
                <div className="flex justify-center mb-2">
                  <IconComponent className="text-blue-400" size={20} />
                </div>
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
              </div>
            );
          })}
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="flex flex-col items-center gap-2 text-gray-500">
          <span className="text-xs">Scroll to explore</span>
          <div className="w-5 h-8 border-2 border-gray-600/50 rounded-full flex justify-center">
            <motion.div
              className="w-1 h-2 bg-gradient-to-b from-blue-400 to-cyan-400 rounded-full mt-2"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
